import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Palette,
  Type,
  Layout,
  Music,
  Video,
  Sparkles,
  Share2,
  Loader2,
  Image,
  Upload,
  X,
} from 'lucide-react';
import { VideoTemplate, TemplateSettings } from './TemplatesTab';
import { TemplateAssetPicker } from './TemplateAssetPicker';

interface ProjectFile {
  id: string;
  name: string;
  fileType?: 'video' | 'audio' | 'image';
  thumbnailUrl?: string;
  s3Key?: string;
  s3Url?: string;
  url?: string;
  duration?: number;
}

interface TemplateBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: Partial<VideoTemplate>) => void;
  template?: VideoTemplate | null;
  currentProjectSettings?: Partial<TemplateSettings>;
  projectFiles?: ProjectFile[];
}

const FONTS = [
  'Open Sans',
  'Montserrat',
  'Roboto',
  'Poppins',
  'Lato',
  'Inter',
  'Playfair Display',
  'Lora',
  'Oswald',
  'Raleway',
];

const TRANSITIONS = [
  { value: 'fade', label: 'Fade' },
  { value: 'dissolve', label: 'Dissolve' },
  { value: 'wipe', label: 'Wipe' },
  { value: 'slide', label: 'Slide' },
  { value: 'none', label: 'None' },
];

const POSITIONS = [
  { value: 'top', label: 'Top' },
  { value: 'center', label: 'Center' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
];

const CAPTION_STYLES = [
  { value: 'subtitle', label: 'Subtitle' },
  { value: 'karaoke', label: 'Karaoke (Word by Word)' },
  { value: 'none', label: 'None' },
];

const FILTER_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'grayscale', label: 'Grayscale' },
  { value: 'sepia', label: 'Sepia' },
  { value: 'vintage', label: 'Vintage' },
  { value: 'cinematic', label: 'Cinematic' },
];

const OVERLAY_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'vignette', label: 'Vignette' },
  { value: 'frame', label: 'Frame' },
  { value: 'gradient', label: 'Gradient' },
];

const defaultSettings: TemplateSettings = {
  colors: {
    primary: '#ffffff',
    secondary: '#000000',
    accent: '#3b82f6',
    background: '#000000',
    text: '#ffffff',
  },
  fonts: {
    title: 'Open Sans',
    subtitle: 'Open Sans',
    body: 'Open Sans',
    titleSize: 72,
    subtitleSize: 48,
    bodySize: 36,
  },
  titles: {
    position: 'center',
    style: 'bold',
    shadow: true,
    shadowColor: '#000000',
    animation: 'fade',
  },
  lowerThirds: {
    enabled: false,
    position: 'bottom-left',
    style: 'minimal',
    backgroundColor: 'rgba(0,0,0,0.7)',
  },
  logo: {
    enabled: false,
    url: '',
    s3Key: '',
    name: '',
    position: 'top-right',
    size: 'small',
    opacity: 1,
  },
  overlays: {
    enabled: false,
    type: 'none',
    color: '#000000',
    opacity: 0.5,
  },
  filters: {
    enabled: false,
    type: 'none',
    intensity: 1,
  },
  background: {
    type: 'color',
    color: '#000000',
    url: '',
    blur: 0,
  },
  audio: {
    musicTrackUrl: '',
    musicTrackS3Key: '',
    musicTrackName: '',
    musicVolume: 0.3,
    voiceVolume: 1.0,
    fadeIn: true,
    fadeOut: true,
    normalize: true,
  },
  timeline: {
    introClipUrl: '',
    introClipS3Key: '',
    introClipName: '',
    introDuration: 0,
    outroClipUrl: '',
    outroClipS3Key: '',
    outroClipName: '',
    outroDuration: 0,
    defaultTransition: 'fade',
    transitionDuration: 0.5,
    defaultClipDuration: 5,
    defaultCaptionDuration: 3,
  },
  aiDefaults: {
    captionStyle: 'subtitle',
    captionFont: 'Open Sans',
    captionSize: 36,
    captionColor: '#ffffff',
    captionBackground: 'rgba(0,0,0,0.7)',
    captionPosition: 'bottom',
    safeArea: true,
    aspectRatio: '9:16',
    resolution: '1080x1920',
    promptPatterns: [],
  },
  social: {
    defaultCaption: '',
    defaultHashtags: [],
    defaultTitle: '',
  },
};

export const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  isOpen,
  onClose,
  onSave,
  template,
  currentProjectSettings,
  projectFiles = [],
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [settings, setSettings] = useState<TemplateSettings>(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('colors');

  // Asset picker modals
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [showIntroPicker, setShowIntroPicker] = useState(false);
  const [showOutroPicker, setShowOutroPicker] = useState(false);
  const [showMusicPicker, setShowMusicPicker] = useState(false);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setSettings({ ...defaultSettings, ...template.settings });
    } else if (currentProjectSettings) {
      setName('New Template');
      setDescription('');
      setSettings({ ...defaultSettings, ...currentProjectSettings });
    } else {
      setName('New Template');
      setDescription('');
      setSettings(defaultSettings);
    }
  }, [template, currentProjectSettings]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({ name, description, settings });
    } finally {
      setSaving(false);
    }
  };

  const updateColors = (key: string, value: string) => {
    setSettings((prev) => ({
      ...prev,
      colors: { ...prev.colors, [key]: value },
    }));
  };

  const updateFonts = (key: string, value: string | number) => {
    setSettings((prev) => ({
      ...prev,
      fonts: { ...prev.fonts, [key]: value },
    }));
  };

  const updateTitles = (key: string, value: string | boolean) => {
    setSettings((prev) => ({
      ...prev,
      titles: { ...prev.titles, [key]: value },
    }));
  };

  const updateOverlays = (key: string, value: string | boolean | number) => {
    setSettings((prev) => ({
      ...prev,
      overlays: { ...prev.overlays, [key]: value },
    }));
  };

  const updateFilters = (key: string, value: string | boolean | number) => {
    setSettings((prev) => ({
      ...prev,
      filters: { ...prev.filters, [key]: value },
    }));
  };

  const updateLogo = (key: string, value: string | boolean | number) => {
    setSettings((prev) => ({
      ...prev,
      logo: { ...prev.logo, [key]: value },
    }));
  };

  const updateAudio = (key: string, value: string | boolean | number) => {
    setSettings((prev) => ({
      ...prev,
      audio: { ...prev.audio, [key]: value },
    }));
  };

  const updateTimeline = (key: string, value: string | number) => {
    setSettings((prev) => ({
      ...prev,
      timeline: { ...prev.timeline, [key]: value },
    }));
  };

  const updateAiDefaults = (key: string, value: string | boolean | number | string[]) => {
    setSettings((prev) => ({
      ...prev,
      aiDefaults: { ...prev.aiDefaults, [key]: value },
    }));
  };

  const updateSocial = (key: string, value: string | string[]) => {
    setSettings((prev) => ({
      ...prev,
      social: { ...prev.social, [key]: value },
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'Create Template'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Template"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
              />
            </div>
          </div>

          {/* Settings Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="colors" className="text-xs">
                <Palette className="h-3 w-3 mr-1" />
                Colors
              </TabsTrigger>
              <TabsTrigger value="fonts" className="text-xs">
                <Type className="h-3 w-3 mr-1" />
                Fonts
              </TabsTrigger>
              <TabsTrigger value="layout" className="text-xs">
                <Layout className="h-3 w-3 mr-1" />
                Layout
              </TabsTrigger>
              <TabsTrigger value="audio" className="text-xs">
                <Music className="h-3 w-3 mr-1" />
                Audio
              </TabsTrigger>
              <TabsTrigger value="timeline" className="text-xs">
                <Video className="h-3 w-3 mr-1" />
                Timeline
              </TabsTrigger>
              <TabsTrigger value="ai" className="text-xs">
                <Sparkles className="h-3 w-3 mr-1" />
                AI
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[350px] mt-4 pr-4">
              {/* Colors Tab */}
              <TabsContent value="colors" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.colors.primary}
                        onChange={(e) => updateColors('primary', e.target.value)}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={settings.colors.primary}
                        onChange={(e) => updateColors('primary', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.colors.secondary}
                        onChange={(e) => updateColors('secondary', e.target.value)}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={settings.colors.secondary}
                        onChange={(e) => updateColors('secondary', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Accent Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.colors.accent}
                        onChange={(e) => updateColors('accent', e.target.value)}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={settings.colors.accent}
                        onChange={(e) => updateColors('accent', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.colors.background}
                        onChange={(e) => updateColors('background', e.target.value)}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={settings.colors.background}
                        onChange={(e) => updateColors('background', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.colors.text}
                        onChange={(e) => updateColors('text', e.target.value)}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={settings.colors.text}
                        onChange={(e) => updateColors('text', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: settings.colors.background }}>
                  <p className="text-lg font-bold" style={{ color: settings.colors.primary }}>
                    Primary Text Preview
                  </p>
                  <p className="text-sm" style={{ color: settings.colors.text }}>
                    Regular text color preview
                  </p>
                  <p className="text-sm mt-1" style={{ color: settings.colors.accent }}>
                    Accent color highlight
                  </p>
                </div>
              </TabsContent>

              {/* Fonts Tab */}
              <TabsContent value="fonts" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Title Font</Label>
                    <Select value={settings.fonts.title} onValueChange={(v) => updateFonts('title', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONTS.map((font) => (
                          <SelectItem key={font} value={font}>
                            {font}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Title Size</Label>
                    <Input
                      type="number"
                      value={settings.fonts.titleSize}
                      onChange={(e) => updateFonts('titleSize', parseInt(e.target.value) || 72)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtitle Font</Label>
                    <Select value={settings.fonts.subtitle} onValueChange={(v) => updateFonts('subtitle', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONTS.map((font) => (
                          <SelectItem key={font} value={font}>
                            {font}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Subtitle Size</Label>
                    <Input
                      type="number"
                      value={settings.fonts.subtitleSize}
                      onChange={(e) => updateFonts('subtitleSize', parseInt(e.target.value) || 48)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Body Font</Label>
                    <Select value={settings.fonts.body} onValueChange={(v) => updateFonts('body', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONTS.map((font) => (
                          <SelectItem key={font} value={font}>
                            {font}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Body Size</Label>
                    <Input
                      type="number"
                      value={settings.fonts.bodySize}
                      onChange={(e) => updateFonts('bodySize', parseInt(e.target.value) || 36)}
                    />
                  </div>
                </div>

                {/* Font Preview */}
                <div
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: settings.colors.background }}
                >
                  <p
                    style={{
                      fontFamily: settings.fonts.title,
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: settings.colors.primary,
                    }}
                  >
                    Title Font Preview
                  </p>
                  <p
                    style={{
                      fontFamily: settings.fonts.subtitle,
                      fontSize: '18px',
                      color: settings.colors.text,
                    }}
                  >
                    Subtitle font preview text
                  </p>
                  <p
                    style={{
                      fontFamily: settings.fonts.body,
                      fontSize: '14px',
                      color: settings.colors.text,
                    }}
                  >
                    Body font preview for regular content text.
                  </p>
                </div>
              </TabsContent>

              {/* Layout Tab */}
              <TabsContent value="layout" className="space-y-4 mt-0">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Title Position</Label>
                      <Select
                        value={settings.titles.position}
                        onValueChange={(v) => updateTitles('position', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {POSITIONS.map((pos) => (
                            <SelectItem key={pos.value} value={pos.value}>
                              {pos.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Note: Removed non-working settings:
                      - Title Animation (not supported by Shotstack)
                      - Text Shadow (not supported by Shotstack)
                      - Overlay effects (not implemented)
                      - Filter effects (not implemented - use Export Effects instead)
                  */}

                  {/* Logo Watermark */}
                  <div className="space-y-2 pt-2 border-t">
                    <Label className="font-medium">Logo Watermark</Label>
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={settings.logo.enabled}
                        onCheckedChange={(v) => updateLogo('enabled', v)}
                      />
                      <span className="text-sm text-muted-foreground">
                        {settings.logo.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    {settings.logo.enabled && (
                      <div className="space-y-3 mt-2">
                        <div className="space-y-2">
                          <Label className="text-xs">Logo Image</Label>
                          {settings.logo.url ? (
                            <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                              <Image className="w-4 h-4 text-muted-foreground" />
                              <span className="flex-1 text-sm truncate">{settings.logo.name || 'Logo'}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  updateLogo('url', '');
                                  updateLogo('s3Key', '');
                                  updateLogo('name', '');
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={() => setShowLogoPicker(true)}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Select Logo
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-2">
                            <Label className="text-xs">Position</Label>
                            <Select
                              value={settings.logo.position}
                              onValueChange={(v) => updateLogo('position', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {POSITIONS.map((pos) => (
                                  <SelectItem key={pos.value} value={pos.value}>
                                    {pos.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Size</Label>
                            <Select
                              value={settings.logo.size}
                              onValueChange={(v) => updateLogo('size', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="small">Small</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="large">Large</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Opacity: {Math.round(settings.logo.opacity * 100)}%</Label>
                          <Slider
                            value={[settings.logo.opacity]}
                            onValueChange={([v]) => updateLogo('opacity', v)}
                            min={0}
                            max={1}
                            step={0.05}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Audio Tab */}
              <TabsContent value="audio" className="space-y-4 mt-0">
                <div className="space-y-4">
                  {/* Background Music Track */}
                  <div className="space-y-2">
                    <Label className="font-medium">Background Music</Label>
                    {settings.audio.musicTrackUrl ? (
                      <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                        <Music className="w-4 h-4 text-muted-foreground" />
                        <span className="flex-1 text-sm truncate">{settings.audio.musicTrackName || 'Music Track'}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            updateAudio('musicTrackUrl', '');
                            updateAudio('musicTrackS3Key', '');
                            updateAudio('musicTrackName', '');
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setShowMusicPicker(true)}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Select Music Track
                      </Button>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Music Volume: {Math.round(settings.audio.musicVolume * 100)}%</Label>
                    <Slider
                      value={[settings.audio.musicVolume]}
                      onValueChange={([v]) => updateAudio('musicVolume', v)}
                      min={0}
                      max={1}
                      step={0.05}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Voice Volume: {Math.round(settings.audio.voiceVolume * 100)}%</Label>
                    <Slider
                      value={[settings.audio.voiceVolume]}
                      onValueChange={([v]) => updateAudio('voiceVolume', v)}
                      min={0}
                      max={1}
                      step={0.05}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>Fade In</Label>
                    <Switch
                      checked={settings.audio.fadeIn}
                      onCheckedChange={(v) => updateAudio('fadeIn', v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>Fade Out</Label>
                    <Switch
                      checked={settings.audio.fadeOut}
                      onCheckedChange={(v) => updateAudio('fadeOut', v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>Normalize Audio</Label>
                    <Switch
                      checked={settings.audio.normalize}
                      onCheckedChange={(v) => updateAudio('normalize', v)}
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Transition</Label>
                    <Select
                      value={settings.timeline.defaultTransition}
                      onValueChange={(v) => updateTimeline('defaultTransition', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TRANSITIONS.map((trans) => (
                          <SelectItem key={trans.value} value={trans.value}>
                            {trans.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Transition Duration (s)</Label>
                    <Input
                      type="number"
                      value={settings.timeline.transitionDuration}
                      onChange={(e) =>
                        updateTimeline('transitionDuration', parseFloat(e.target.value) || 0.5)
                      }
                      step={0.1}
                      min={0}
                      max={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Default Clip Duration (s)</Label>
                    <Input
                      type="number"
                      value={settings.timeline.defaultClipDuration}
                      onChange={(e) =>
                        updateTimeline('defaultClipDuration', parseInt(e.target.value) || 5)
                      }
                      min={1}
                      max={30}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Default Caption Duration (s)</Label>
                    <Input
                      type="number"
                      value={settings.timeline.defaultCaptionDuration}
                      onChange={(e) =>
                        updateTimeline('defaultCaptionDuration', parseInt(e.target.value) || 3)
                      }
                      min={1}
                      max={10}
                    />
                  </div>
                </div>

                {/* Intro Clip */}
                <div className="space-y-2 pt-3 border-t">
                  <Label className="font-medium">Intro Clip</Label>
                  <p className="text-xs text-muted-foreground">Video clip that plays before your content</p>
                  {settings.timeline.introClipUrl ? (
                    <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                      <Video className="w-4 h-4 text-muted-foreground" />
                      <span className="flex-1 text-sm truncate">{settings.timeline.introClipName || 'Intro Clip'}</span>
                      {settings.timeline.introDuration > 0 && (
                        <span className="text-xs text-muted-foreground">{settings.timeline.introDuration}s</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          updateTimeline('introClipUrl', '');
                          updateTimeline('introClipS3Key', '');
                          updateTimeline('introClipName', '');
                          updateTimeline('introDuration', 0);
                        }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowIntroPicker(true)}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Select Intro Clip
                    </Button>
                  )}
                </div>

                {/* Outro Clip */}
                <div className="space-y-2 pt-3 border-t">
                  <Label className="font-medium">Outro Clip</Label>
                  <p className="text-xs text-muted-foreground">Video clip that plays after your content</p>
                  {settings.timeline.outroClipUrl ? (
                    <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                      <Video className="w-4 h-4 text-muted-foreground" />
                      <span className="flex-1 text-sm truncate">{settings.timeline.outroClipName || 'Outro Clip'}</span>
                      {settings.timeline.outroDuration > 0 && (
                        <span className="text-xs text-muted-foreground">{settings.timeline.outroDuration}s</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          updateTimeline('outroClipUrl', '');
                          updateTimeline('outroClipS3Key', '');
                          updateTimeline('outroClipName', '');
                          updateTimeline('outroDuration', 0);
                        }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowOutroPicker(true)}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Select Outro Clip
                    </Button>
                  )}
                </div>
              </TabsContent>

              {/* AI Defaults Tab */}
              <TabsContent value="ai" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Caption Style</Label>
                    <Select
                      value={settings.aiDefaults.captionStyle}
                      onValueChange={(v) => updateAiDefaults('captionStyle', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CAPTION_STYLES.map((style) => (
                          <SelectItem key={style.value} value={style.value}>
                            {style.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Caption Font</Label>
                    <Select
                      value={settings.aiDefaults.captionFont}
                      onValueChange={(v) => updateAiDefaults('captionFont', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONTS.map((font) => (
                          <SelectItem key={font} value={font}>
                            {font}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Caption Size</Label>
                    <Input
                      type="number"
                      value={settings.aiDefaults.captionSize}
                      onChange={(e) =>
                        updateAiDefaults('captionSize', parseInt(e.target.value) || 36)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Caption Position</Label>
                    <Select
                      value={settings.aiDefaults.captionPosition}
                      onValueChange={(v) => updateAiDefaults('captionPosition', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="top">Top</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="bottom">Bottom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Caption Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.aiDefaults.captionColor}
                        onChange={(e) => updateAiDefaults('captionColor', e.target.value)}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={settings.aiDefaults.captionColor}
                        onChange={(e) => updateAiDefaults('captionColor', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Aspect Ratio</Label>
                    <Select
                      value={settings.aiDefaults.aspectRatio}
                      onValueChange={(v) => updateAiDefaults('aspectRatio', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="9:16">9:16 (Vertical)</SelectItem>
                        <SelectItem value="16:9">16:9 (Horizontal)</SelectItem>
                        <SelectItem value="1:1">1:1 (Square)</SelectItem>
                        <SelectItem value="4:5">4:5 (Portrait)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Label>Safe Area (for social media)</Label>
                  <Switch
                    checked={settings.aiDefaults.safeArea}
                    onCheckedChange={(v) => updateAiDefaults('safeArea', v)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Default Hashtags (for scheduling)</Label>
                  <Textarea
                    value={settings.social.defaultHashtags.join(', ')}
                    onChange={(e) =>
                      updateSocial(
                        'defaultHashtags',
                        e.target.value
                          .split(',')
                          .map((t) => t.trim())
                          .filter((t) => t)
                      )
                    }
                    placeholder="#video #content #social"
                  />
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {template ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Asset Picker Modals */}
      <TemplateAssetPicker
        isOpen={showLogoPicker}
        onClose={() => setShowLogoPicker(false)}
        onSelect={(asset) => {
          updateLogo('url', asset.url);
          updateLogo('s3Key', asset.s3Key);
          updateLogo('name', asset.name);
        }}
        assetType="image"
        title="Select Logo Image"
        projectFiles={projectFiles}
      />

      <TemplateAssetPicker
        isOpen={showIntroPicker}
        onClose={() => setShowIntroPicker(false)}
        onSelect={(asset) => {
          updateTimeline('introClipUrl', asset.url);
          updateTimeline('introClipS3Key', asset.s3Key);
          updateTimeline('introClipName', asset.name);
          if (asset.duration) {
            updateTimeline('introDuration', asset.duration);
          }
        }}
        assetType="video"
        title="Select Intro Clip"
        projectFiles={projectFiles}
      />

      <TemplateAssetPicker
        isOpen={showOutroPicker}
        onClose={() => setShowOutroPicker(false)}
        onSelect={(asset) => {
          updateTimeline('outroClipUrl', asset.url);
          updateTimeline('outroClipS3Key', asset.s3Key);
          updateTimeline('outroClipName', asset.name);
          if (asset.duration) {
            updateTimeline('outroDuration', asset.duration);
          }
        }}
        assetType="video"
        title="Select Outro Clip"
        projectFiles={projectFiles}
      />

      <TemplateAssetPicker
        isOpen={showMusicPicker}
        onClose={() => setShowMusicPicker(false)}
        onSelect={(asset) => {
          updateAudio('musicTrackUrl', asset.url);
          updateAudio('musicTrackS3Key', asset.s3Key);
          updateAudio('musicTrackName', asset.name);
        }}
        assetType="audio"
        title="Select Background Music"
        projectFiles={projectFiles}
      />
    </Dialog>
  );
};
